// ── <PreprocessVariantDebugRow> — pick which preprocessor authors the next question ──
//
// A dropdown over the authoring strategies ("variants") the contract server ADVERTISES on
// `GET /preprocess/variants`. Selecting one makes the next submit's `POST /preprocess/auto`
// dispatch to that strategy instead of the deployment default, so two preprocessors can be
// compared against integration/staging by asking the same question twice — which is the
// whole point: the offline eval matrix measures strategies, and this is how one gets driven
// by hand, in the real UI, before it is promoted.
//
// WHY IT IS SAFE TO PUT IN A BROWSER. The dropdown's values are IDS, and the only thing
// that crosses the wire is one of them. Prompts live in reviewed Python
// (contract_server/variants.py) and are addressed by id; there is no field on the request
// that prompt text could arrive in, and an id the server does not have registered is
// refused rather than silently defaulted. So the worst a hostile client can do is name a
// strategy the team already ships.
//
// WHY IT IS NOT AVAILABLE IN PROD. Extracted as a PURE-PROPS component for the same reason
// CopyLinksDebugRow was — SettingsModal's ctx is too large to stub — and it inherits that
// row's first rule: `available !== true` RENDERS NOTHING, never a disabled control. But the
// gate behind `available` is a stronger one than the copy-link row's. It is the AND of the
// per-browser Debug reveal AND /api/me's `debugUIEnabled` (EXPOSE_DEBUG_UI, unset in
// partner-facing prod, ANDed with the `chatboard-debug-ui` flag) — see
// hooks/usePreprocessVariant.js. The Debug reveal alone would NOT be enough: it works on
// every deployment including prod, and this control changes what the pipeline runs.
//
// And, as with the copy-link routes: hiding a control is not a gate. The chatboard server
// re-decides per request (`resolvePreprocessVariantGate` in server.js) and drops a
// `variant_id` any deployment is not allowed to honour, which is what actually holds.

import { C } from "../lib/colors.js";
import { defaultOptionLabel } from "../lib/preprocessVariantLabel.js";
import { ui } from "../lib/telemetry/instrument.js";

/** The row's label, exported so a test can name it without re-typing the string. */
export const PREPROCESS_VARIANT_ROW_LABEL = "Preprocessor variant";

/** The value of the "use the deployment default" option — the empty selection. */
export const PREPROCESS_VARIANT_DEFAULT_VALUE = "";

/**
 * The variant picker.
 *
 * @param {object} props
 * @param {unknown} [props.available] The composed gate from hooks/usePreprocessVariant.js.
 *   Anything other than boolean `true` renders nothing.
 * @param {string} [props.value] The current selection; "" = no override.
 * @param {(id: string) => void} [props.onChange] Called with the new id (or "" to clear).
 * @param {Array<object>} [props.variants] The advertised listing, as served.
 * @param {string} [props.deploymentDefault] This deployment's flag value ("" = registry default).
 * @param {boolean} [props.loading] True while the listing is in flight.
 * @param {boolean} [props.proColors] The pro-colour palette, threaded from SettingsModal's ctx.
 */
export default function PreprocessVariantDebugRow({
  available,
  value = "",
  onChange,
  variants = [],
  deploymentDefault = "",
  loading = false,
  proColors = false,
}) {
  if (available !== true) return null;
  const accent = proColors ? C.purple : "#33aa88";
  const overridden = value !== PREPROCESS_VARIANT_DEFAULT_VALUE;
  // No listing means either a deployment that refuses the endpoint (404 — in which case we
  // should not be rendering at all) or a failed fetch. Say so instead of offering a
  // dropdown whose only entry is "Default", which reads as "there is one strategy".
  const empty = !loading && variants.length === 0;

  return (
    <div
      className="settings-toggle-row"
      data-testid="preprocess-variant-debug-row"
      style={{ alignItems: "flex-start", gap: 10 }}
    >
      <div style={{ flex: 1, minWidth: 140 }}>
        <div className="settings-toggle-label" style={{ color: overridden ? accent : undefined }}>
          {PREPROCESS_VARIANT_ROW_LABEL}
        </div>
        <div className="settings-hint">
          {empty
            ? "Could not load the advertised strategies"
            : "Which strategy authors the answer space for the next question you submit"}
        </div>
        {/* The selected strategy's own description + model, from the listing. Shown only
            for an override: it is the thing you need to read to know what you just
            selected, and it would be noise on the default. */}
        {overridden &&
          (() => {
            const v = variants.find((x) => x.id === value);
            if (!v) return null;
            return (
              <div
                className="settings-hint"
                data-testid="preprocess-variant-detail"
                style={{ marginTop: 4, color: C.gray }}
              >
                {v.description} · {v.model}
              </div>
            );
          })()}
      </div>
      <select
        data-testid="preprocess-variant-select"
        {...ui("settings.debug-preprocess-variant")}
        aria-label={PREPROCESS_VARIANT_ROW_LABEL}
        disabled={loading || empty}
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        style={{
          background: C.inputBg,
          border: `1px solid ${overridden ? accent : C.border}`,
          borderRadius: 6,
          color: C.white,
          fontFamily: '"IBM Plex Mono",monospace',
          fontSize: 11,
          padding: "3px 6px",
          outline: "none",
          cursor: loading || empty ? "default" : "pointer",
          maxWidth: 220,
        }}
      >
        <option value={PREPROCESS_VARIANT_DEFAULT_VALUE}>
          {loading ? "Loading…" : defaultOptionLabel(deploymentDefault, variants)}
        </option>
        {variants.map((v) => (
          <option key={v.id} value={v.id}>
            {v.label}
          </option>
        ))}
      </select>
    </div>
  );
}
